import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap, takeUntil } from 'rxjs/operators';

// Interface
import { IInvoiceType } from '../../../invoice/interfaces/invoice.interface';
import { EOrderTypeId } from '../../../order/interfaces';

// Services
import { InvoiceApiService } from './../../../shared/services/api/invoice-api.service';

interface IResponse {
  data :IInvoiceType[];
  links :any;
  meta :any;
}

@Component({
  selector: 'lum-filter-client-purchase-order-type',
  templateUrl: './filter-client-purchase-order-type.component.html',
  styleUrls: ['./filter-client-purchase-order-type.component.scss'],
})
export class FilterClientPurchaseOrderTypeComponent implements OnInit {
  @Input()
  label :string = '';

  @Input()
  displayValue :string = '';

  @Input()
  disableDefaultValue :boolean = false;

  @Output()
  selectInvoiceEvent = new EventEmitter<IInvoiceType>();

  invoiceTypes :IInvoiceType[] = [];
  params :{ search ?:string; per_page :string; page :string } = {
    per_page: '100',
    page: '1',
  };

  invoiceTypes$ :Observable<IResponse>;
  invoiceTypesLoading$ :Observable<boolean>;

  private searchUpdated$ = new Subject<string>();
  private onDestroy$ = new Subject();

  constructor(private invoiceApiService :InvoiceApiService) {}

  ngOnInit() :void {
    this.initActions();
    this.getDataFromService();
    this.onSearchUpdate().subscribe();
    this.getInvoiceTypes().subscribe();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onSearchInputChange(event :any) :void {
    this.searchUpdated$.next(event.target.value);
  }

  onSelectOption(event :MatAutocompleteSelectedEvent) :void {
    this.selectInvoiceEvent.emit(event.option?.value);
  }

  displayFn(option :any) :string {
    return option && option.label ? option.label : '';
  }

  private initActions() :void {
    this.invoiceApiService.getInvoiceTypes(this.params);
  }

  private getDataFromService() :void {
    this.invoiceTypes$ = this.invoiceApiService.invoiceTypes$;
    this.invoiceTypesLoading$ = this.invoiceApiService.invoiceTypesLoading$;
  }

  private onSearchUpdate() :Observable<string> {
    return this.searchUpdated$.asObservable().pipe(
      debounceTime(500),
      distinctUntilChanged(),
      tap((keyword :string) => {
        this.invoiceApiService.getInvoiceTypes({
          ...this.params,
          search: keyword,
        });
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getInvoiceTypes() :Observable<IResponse> {
    return this.invoiceTypes$.pipe(
      tap((response :IResponse) => {
        this.invoiceTypes = response?.data || [];
      }),
      takeUntil(this.onDestroy$),
    );
  }

  get textValue() :string {
    const invoiceType = this.invoiceTypes.find(
      (type) => type.id === EOrderTypeId.DEFAULT,
    );

    if (this.displayValue === '' && this.disableDefaultValue) {
      return '';
    }

    return this.displayValue || invoiceType?.label || '';
  }
}
